import { Component, computed, EventEmitter, inject, input, LOCALE_ID, Output, signal } from "@angular/core";
import { toSignal } from "@angular/core/rxjs-interop";
import { ReactiveFormsModule } from "@angular/forms";
import { CloudProjectOdxTableService } from "@components/cloud-project-odx-tables/cloud-project-odx-table.service";
import { CloudProjectMetadata } from "@domain/project/cloud-project-metadata";
import { LocalizeProjectTypePipe } from "@domain/project/localize-project-type.pipe";
import { ProjectStatusIconMapperService } from "@domain/project/project-status-icon-mapper.service";
import { ProjectStatusService } from "@domain/project/project-status.service";
import { ProjectType } from "@domain/project/project-type.enum";
import { ProjectStatusDto } from "@domain/project/template/property/project/details/information/project-status.dto";
import { UserService } from "@domain/user/user.service";
import { CoreModule } from "@odx/angular";
import { DynamicTextContent } from "@odx/angular/cdk/dynamic-view";
import { ChipComponent } from "@odx/angular/components/chip";
import { FormFieldModule } from "@odx/angular/components/form-field";
import { IconComponent } from "@odx/angular/components/icon";
import { PageChangeEvent, PaginatorModule } from "@odx/angular/components/paginator";
import { SortStatus, TableComponent, TableHeaderCell, TableModule, TableSortVariant } from "@odx/angular/components/table";
import { TooltipDirective } from "@odx/angular/components/tooltip";
import { map, Observable, shareReplay, switchMap } from "rxjs";

interface TableData {
  displayName: string;
  customerName: string;
  lastModifiedOn: string;
  projectType: string[];
  projectStatus: string;
  icon: string;
  cloudProjectMetadata: CloudProjectMetadata;
}

@Component({
  selector: "app-import-cloud-projects-odx-table",
  templateUrl: "import-cloud-projects-odx-table.component.html",
  imports: [
    CoreModule,
    TableModule,
    ReactiveFormsModule,
    FormFieldModule,
    TableComponent,
    PaginatorModule,
    IconComponent,
    TooltipDirective,
    ChipComponent,
  ],
  styleUrls: ["import-cloud-projects-odx-table.component.scss"],
  standalone: true,
})
export class ImportCloudProjectsOdxTableComponent {
  private localeId = inject(LOCALE_ID);
  private userService = inject(UserService);
  private tableService = inject(CloudProjectOdxTableService);
  private projectStatusIconMapperService: ProjectStatusIconMapperService = inject(ProjectStatusIconMapperService);
  private projectStatusService: ProjectStatusService = inject(ProjectStatusService);

  private userId!: string;
  private statuses$: Observable<ProjectStatusDto[]> = this.userService.businessScope$.pipe(
    switchMap((scope) => this.projectStatusService.getProjectStatuses(scope)),
    shareReplay({ bufferSize: 1, refCount: true }),
  );

  readonly statuses = toSignal<ProjectStatusDto[] | undefined>(this.statuses$, {
    initialValue: undefined,
  });

  private localizeProjectTypePipe = new LocalizeProjectTypePipe();

  protected userId$: Observable<string>;

  protected selected?: CloudProjectMetadata;

  protected total = 0;
  protected pageIndex = 0;
  protected readonly tableSize = 10;

  protected readonly headerData: TableHeaderCell[] = [
    { name: "displayName", title: $localize`:@@cloudProjectsTable.column1.header:Projektname`, sortable: true, filter: true },
    {
      name: "customerName",
      title: $localize`:@@cloudProjectsTable.header.customerName:Kundenname`,
      sortable: true,
      filter: true,
    },
    {
      name: "lastModifiedOn",
      title: $localize`:@@cloudProjectsTable.column2.header:Zuletzt synchronisiert`,
      sortable: true,
      filter: true,
    },
    {
      name: "projectType",
      title: $localize`:@@cloudProjectsTable.column3.header:Projekttyp`,
      filter: true,
    },
    {
      name: "projectStatus",
      title: $localize`:@@cloudProjectsTable.header.projectStatus:Projektstatus`,
      filter: true,
    },
    { name: "cloudProjectMetadata", title: "", sortable: false, filter: false }, // Workaround to display no header above the last column as otherwise odx automatically generates the header.
  ];

  readonly cloudProjectMetadata = input<CloudProjectMetadata[]>([]);

  totalData = computed(() => {
    const statuses = this.statuses();
    const metadata = this.cloudProjectMetadata();
    if (!statuses || !metadata) {
      return [];
    }
    try {
      return this.cloudProjectMetadata().map((metadata) => ({
        displayName: metadata.displayName,
        customerName: metadata.customerName,
        lastModifiedOn: metadata.lastModifiedOn,
        projectType: this.setProjectType(metadata)!,
        projectStatus: metadata.status,
        icon: this.projectStatusIconMapperService.mapIcon(metadata.status, statuses),
        cloudProjectMetadata: metadata,
      }));
    } catch (e) {
      console.error("totalData computed error:", e);
      return [];
    }
  });

  protected dataToDisplay = computed<TableData[]>(() => this.generateDataToDisplay());

  readonly sortParams = signal<SortStatus>({
    column: "lastModifiedOn",
    sortVariant: TableSortVariant.DESC,
  });
  readonly filterParams = signal<Record<string, string>>({});
  readonly paginationParams = signal({ startIndex: 0, endIndex: this.tableSize });

  @Output() projectSelected = new EventEmitter<CloudProjectMetadata>();

  constructor() {
    this.statuses$.subscribe();
    this.userId$ = this.userService.user$.pipe(map((user) => user.id));

    this.userId$.subscribe((id) => {
      this.userId = id;
    });
  }

  public sorted(sortParams: SortStatus): void {
    this.sortParams.update(() => sortParams);
  }

  public filter(filterParams: Record<string, string>): void {
    this.pageIndex = 0;
    this.filterParams.update(() => filterParams);

    this.paginationParams.update((p) => ({
      ...p,
      startIndex: 0,
      endIndex: this.tableSize,
    }));
  }

  public pageChanged(event: PageChangeEvent): void {
    this.pageIndex = event.pageIndex;
    this.paginationParams.update((p) => ({
      ...p,
      startIndex: event.pageIndex * this.tableSize,
      endIndex: (event.pageIndex + 1) * this.tableSize,
    }));
  }

  protected onClickRow(cloudProjectMetadata: CloudProjectMetadata) {
    this.selected = cloudProjectMetadata;
    this.projectSelected.emit(cloudProjectMetadata);
  }

  protected getSummaryContentFn = (startItemIndex: number, endItemIndex: number, length: number): DynamicTextContent => {
    return this.tableService.getSummaryContentFn(startItemIndex, endItemIndex, length);
  };

  protected getRangeContentFn = (pageIndex: number, totalPages: number): DynamicTextContent => {
    return this.tableService.getRangeContentFn(pageIndex, totalPages);
  };

  private generateDataToDisplay(): TableData[] {
    const rows = this.totalData();
    const sorted = this.tableService.sortData<TableData>(this.sortParams(), rows);
    const converted = this.tableService.convertLastModifiedOn<TableData>(sorted);
    const filtered = this.tableService.filterData(this.filterParams(), converted);
    this.total = filtered.length;

    const { startIndex, endIndex } = this.paginationParams();
    return filtered.slice(startIndex, endIndex);
  }

  protected convertTimestampToDate(timestamp: string) {
    return this.tableService.convertTimestamp(timestamp, this.localeId);
  }

  private setProjectType(data: CloudProjectMetadata) {
    const types: string[] = [];
    const rawUser = data.users[0] as any;

    if (rawUser.id === this.userId && rawUser.role === "OWNER") {
      types.push(this.localizeProjectTypePipe.transform(ProjectType.OWNED));
    } else {
      types.push(this.localizeProjectTypePipe.transform(ProjectType.NOT_OWNED));
    }

    if (data.users.length > 1) types.push(this.localizeProjectTypePipe.transform(ProjectType.COLLABORATION));

    return types;
  }
}
